'use client';

// Het meldvenster achter de Research Agent. Eenmalig gemonteerd in app/layout.js,
// naast AppDialogHost en om dezelfde reden: de melder zit in lib.js (de avatar,
// de bovenbalk) en dit bestand leest Modal.js, dat op zijn beurt lib.js leest.
// Een directe import terug zou een import-cirkel geven. lib.js roept daarom
// openAgentReport() aan en dit venster luistert via _subscribeAgentReport.
//
// Omdat het venster in de layout hangt en niet in de klikkende rij, werkt melden
// overal hetzelfde: vanaf de bovenbalk, vanaf een eigenaarskolom in een CRM-raster
// en vanuit een detailvenster, zonder dat een overflow van de rij het afknipt.

import { useCallback, useEffect, useState } from 'react';
import { HV2, Select, inputStyle, t, _subscribeAgentReport, MenuIcon } from './lib';
import Button from './Button';
import Modal from './Modal';

const SEVERITIES = [['laag', 'Laag'], ['normaal', 'Normaal'], ['hoog', 'Hoog']];

// Hoe lang de bevestiging blijft staan voordat het venster zichzelf sluit. Lang
// genoeg om te lezen, kort genoeg om niet in de weg te zitten.
const CLOSE_AFTER_MS = 1400;

export default function AgentReportModal() {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState('normaal');
  const [page, setPage] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => _subscribeAgentReport(() => {
    setBody(''); setSeverity('normaal'); setBusy(false); setDone(false); setErr('');
    // De pagina wordt bij het OPENEN vastgelegd, niet bij het versturen: iemand die
    // tijdens het typen doorklikt zou anders de verkeerde pagina melden.
    setPage(typeof window !== 'undefined' ? (window.location.hash || window.location.pathname) : '');
    setOpen(true);
  }), []);

  const close = useCallback(() => setOpen(false), []);

  // Sluit vanzelf zodra de bevestiging is gezien.
  useEffect(() => {
    if (!done) return undefined;
    const id = setTimeout(() => setOpen(false), CLOSE_AFTER_MS);
    return () => clearTimeout(id);
  }, [done]);

  const submit = useCallback(async () => {
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true); setErr('');
    try {
      const res = await fetch('/api/agent-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body: text,
          severity,
          url: typeof window !== 'undefined' ? window.location.href : '',
          pageHash: typeof window !== 'undefined' ? window.location.hash : '',
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.error) throw new Error((data && data.error) || 'Versturen mislukt.');
      setDone(true);
    } catch (e) {
      setErr(e?.message || 'Versturen mislukt.');
    } finally {
      setBusy(false);
    }
  }, [body, severity, busy]);

  if (!open) return null;

  const label = { fontSize: HV2.fsText, fontWeight: 600, color: HV2.ink };

  // zIndex 1300 is de bovenste laag die de app voor vensters gebruikt
  // (CrmDatabase). Bewust niet hoger: de Select-popover portalt op 4000 en moet
  // boven dit venster kunnen openen.
  return (
    <Modal open onClose={close} title={t('Bug melden')} width="520px" zIndex={1300} confirmDirty={() => !done && !!body.trim()}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {done ? (
          <div style={{ padding: '10px 12px', border: `1px solid ${HV2.greenBd}`, background: HV2.greenBg, color: HV2.greenText, fontSize: HV2.fsText, fontWeight: 600 }}>
            {t('Melding verstuurd naar Management')}
          </div>
        ) : (<>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={label}>{t('Wat is er aan de hand')}</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} maxLength={4000}
              placeholder={t('Wat ging er mis of wat kan beter?')}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 120, lineHeight: 1.45 }} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={label}>{t('Ernst')}</span>
            <Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {SEVERITIES.map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
            </Select>
          </label>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: HV2.fsText, color: HV2.muted }}>
            <span>{t('Huidige pagina')}</span>
            <span style={{ color: HV2.ink2, wordBreak: 'break-all' }}>{page || t('onbekend')}</span>
          </div>

          {err && (
            <div style={{ padding: '8px 10px', border: `1px solid ${HV2.red}`, background: HV2.redBg, color: HV2.redText, fontSize: HV2.fsText }}>{err}</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button icon={<MenuIcon name="close" />} variant="white" onClick={close}>{t('Annuleren')}</Button>
            <Button icon={<MenuIcon name="send" />} variant="primary" disabled={!body.trim() || busy} onClick={submit}>{t('Melding versturen')}</Button>
          </div>
        </>)}
      </div>
    </Modal>
  );
}
